//Jan27 OrganizationContext2 without updates to aling with AuthContext

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, collection, addDoc } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { useLoading } from './LoadingContext';
import { httpsCallable } from 'firebase/functions'; // For calling Firebase Functions
import { functions } from '../firebase'; // Import your Firebase functions setup
import { isOrganizationOwner } from '../utils/firestore'; // Ownership validation utility
import debounce from 'lodash/debounce';
import { BILLING_POLICY, planConfigs } from 'plan-catalog';

const OrganizationContext2 = createContext();

const SELF_SERVE_TRIAL_DAYS =
  Number.isFinite(Number(BILLING_POLICY?.selfServeTrialDays)) &&
  Number(BILLING_POLICY.selfServeTrialDays) > 0
    ? Number(BILLING_POLICY.selfServeTrialDays)
    : 14;

const buildTrialEndsAtFallback = () =>
  new Date(
    Date.now() + SELF_SERVE_TRIAL_DAYS * 24 * 60 * 60 * 1000
  ).toISOString();

export function useOrganization2() {
  return useContext(OrganizationContext2);
}

export const OrganizationProvider2 = ({ children }) => {
  const [organization, setOrganization] = useState(null);
  const { organizationId, currentUser, initialized, setAuthState, userPermissions, userRole, userProfile } = useAuth();
//  console.log(`[OrganizationContext2] Retrieved from AuthContext: organizationId=${organizationId}, currentUser=${currentUser?.uid}, initialized=${initialized}`);
  const { setLoading } = useLoading();
  const prevOrganizationId = useRef(null); // Track the previous organization ID to avoid unnecessary fetches
  const organizationRef = useRef(null); 

  // Helper function to store organization data in sessionStorage
  const storeOrganizationInCache = (organizationId, organizationData) => {
    sessionStorage.setItem(`organization_${organizationId}`, JSON.stringify(organizationData));
  };

  // Helper function to retrieve organization data from sessionStorage
  const getOrganizationFromCache = (organizationId) => {
    const cachedData = sessionStorage.getItem(`organization_${organizationId}`);
    return cachedData ? JSON.parse(cachedData) : null;
  };

  // Helper function to clear organization data from sessionStorage
  const clearOrganizationCache = (organizationId) => {
    sessionStorage.removeItem(`organization_${organizationId}`);
  };

// Helper function to check if the current user is the owner of the organization
const isCurrentUserOwner = useCallback(async () => {
//  console.log('[OrganizationContext2] Starting ownership validation...');
//  console.log('[OrganizationContext2] Current state values:', {
//    organizationId,
//    currentUserUid: currentUser?.uid,
//    initialized,
//  });

  // Ensure required data is fully initialized and available before proceeding
  if (!initialized) {
    console.warn('[OrganizationContext2] Skipping ownership check: Context is not initialized.');
    return false;
  }

  if (!organizationId || !currentUser?.uid) {
    console.warn('[OrganizationContext2] Cannot check ownership: Missing organizationId or uid.');
    return false;
  }

  try {
//    console.log(
//      `[OrganizationContext2] Validating ownership with organizationId=${organizationId}, uid=${currentUser.uid}`
//    );

    // Call the ownership validation utility with organizationId and uid
    const isOwner = await isOrganizationOwner(organizationId, currentUser.uid);

    // Log the result of the ownership check
//    console.log(`[OrganizationContext2] Ownership validation result: ${isOwner}`);
    return isOwner;
  } catch (error) {
    // Log detailed error information if the check fails
    console.error(
      '[OrganizationContext2] Error during ownership validation:',
      error.message || error
    );
    throw new Error('Failed to validate ownership. Please try again later.');
  }
}, [initialized, organizationId, currentUser?.uid]);

  // Function to fetch organization data, with caching logic
  const fetchOrganizationData = useCallback(async () => {
//    console.log('[OrganizationContext2] fetchOrganizationData triggered');

    if (!initialized || !organizationId) {
      console.warn('[OrganizationContext2] Skipping fetch: Context not initialized or organizationId is missing.');
      return;
    }

    // Check sessionStorage for cached organization data
    const cachedOrg = getOrganizationFromCache(organizationId);
    if (cachedOrg) {
//      console.log('[OrganizationContext2] Using cached organization data:', cachedOrg);
      setOrganization(cachedOrg);
      prevOrganizationId.current = organizationId;
      return;
    }

    // Fetch organization data from Firestore if not in cache
    setLoading(true, 'OrganizationContext2');
    try {
      const orgRef = doc(db, 'organizations', organizationId);
      const orgSnap = await getDoc(orgRef);

      if (orgSnap.exists()) {
        const newOrgData = { id: orgSnap.id, ...orgSnap.data() };
//        console.log('[OrganizationContext2] Fetched organization data from Firestore:', newOrgData);

        // Update state and store in cache
        setOrganization(newOrgData);
        storeOrganizationInCache(organizationId, newOrgData);
        prevOrganizationId.current = organizationId;
      } else {
        console.warn('[OrganizationContext2] Organization does not exist in Firestore.');
      }
    } catch (error) {
      console.error('[OrganizationContext2] Failed to fetch organization data:', error);
    } finally {
      setLoading(false, 'OrganizationContext2');
//      console.log('[OrganizationContext2] fetchOrganizationData completed');
    }
  }, [initialized, organizationId, setLoading]);


const debouncedFetchOrganizationData = useMemo(
  () => debounce(fetchOrganizationData, 300),
  [fetchOrganizationData]
);

useEffect(() => {
  if (initialized && organizationId) {
    debouncedFetchOrganizationData();
  }

  return () => {
    debouncedFetchOrganizationData.cancel();
  };
}, [debouncedFetchOrganizationData, initialized, organizationId]);




const createOrganization = useCallback(async (orgName, userId, email) => {
  setLoading(true, "OrganizationContext2");
  let subscriptionId = null;
  let subscriptionStatus = "inactive"; // Default status if subscription creation fails
  let stripeSubscriptionItemId = null; // Placeholder for subscription item ID

  // ───── default plan / seats pulled from catalog ─────
  const MIN_SEATS = 1; // ← single source of truth
  const defaultPlanKey =
    BILLING_POLICY?.defaultTrialPlanKey || "standard";
  const defaultInterval =
    BILLING_POLICY?.defaultTrialInterval || "monthly";
  const defaultTier =
    planConfigs[defaultPlanKey].pricing[defaultInterval];

  const { priceId, includedSeats = 1 } = defaultTier;

  // ensure at least MIN_SEATS, even if catalog changes
  const defaultSeatCount = Math.max(MIN_SEATS, includedSeats);

  let subscriptionTrialStartsAt = null;
  let subscriptionTrialEndsAt = null;
  let subscriptionPlanKey = defaultPlanKey;
  let subscriptionPriceId = priceId;
  let subscriptionSeatCount = defaultSeatCount;

  try {
    // Step 1: Create Stripe Customer
    const createStripeCustomer = httpsCallable(
      functions,
      "createStripeCustomer"
    );
    const stripeCustomerResponse = await createStripeCustomer({
      name: orgName,
      email,
    });
    const stripeCustomerId = stripeCustomerResponse.data.customerId;

    // Step 2: Create Subscription
    try {
      const createSubscription = httpsCallable(
        functions,
        "createStripeSubscription"
      );
       const subscriptionPayload = {
         customerId: stripeCustomerId,
         priceId,                 // ← from catalog
         seatCount: defaultSeatCount, // ← matches Cloud Function param
       };

      const subscriptionResponse =
        await createSubscription(subscriptionPayload);

      subscriptionId = subscriptionResponse.data.subscriptionId;
      subscriptionStatus = subscriptionResponse.data.status;
      stripeSubscriptionItemId =
        subscriptionResponse.data.stripeSubscriptionItemId;

      subscriptionTrialStartsAt =
        subscriptionResponse.data.trialStartsAt || null;
      subscriptionTrialEndsAt =
        subscriptionResponse.data.trialEndsAt || null;
      subscriptionPlanKey =
        subscriptionResponse.data.planKey || defaultPlanKey;
      subscriptionPriceId =
        subscriptionResponse.data.priceId || priceId;
      subscriptionSeatCount =
        Number.isFinite(Number(subscriptionResponse.data.seatCount))
          ? Number(subscriptionResponse.data.seatCount)
          : defaultSeatCount;
    } catch (error) {
      console.warn(
        "[OrganizationContext2] Failed to create subscription. Proceeding without a subscription:",
        error.message
      );
    }

    // Step 3: Add Organization to Firestore
    const orgRef = await addDoc(collection(db, "organizations"), {
      name: orgName,
      createdBy: userId,
      ownerId: userId,
      ownerEmail: email,
      stripeCustomerId,
      subscriptionId,
      stripeSubscriptionItemId,
      subscriptionStatus,
      trialStartsAt: subscriptionTrialStartsAt,
      trialEndsAt:
        subscriptionStatus === "trialing"
          ? subscriptionTrialEndsAt || buildTrialEndsAtFallback()
          : null,
      hasValidPaymentMethod: false, // Default to false initially
      createdAt: new Date(),
      seatCount: subscriptionSeatCount, // Set default seat count to 1
      lastBilledAmount: 0, // Initialize billing amount
      planName: subscriptionPlanKey,
      planKey: subscriptionPlanKey,
      priceId: subscriptionPriceId,
      interval: defaultInterval,
      pendingStripeChange: null,
      pendingStripeSchedule: null,
      suspendedStripeSchedule: null,
    });

    // Step 4: Sync subscription details with Stripe and update state
    const syncCallable = httpsCallable(
      functions,
      "syncStripeSubscriptionDetails"
    );

    let syncedBillingData = null;

    try {
      const syncResponse = await syncCallable({stripeCustomerId});

      if (syncResponse.data.success) {
        syncedBillingData = syncResponse.data;

        console.log(
            "[OrganizationContext2] Successfully synced subscription " +
            "details during organization creation:",
            syncResponse.data.message,
        );

        console.log(
            "[OrganizationContext2] Synced billing payload during " +
            "organization creation:",
            syncedBillingData,
        );
      } else {
        console.warn(
            "[OrganizationContext2] Syncing subscription details failed:",
            syncResponse.data.message,
        );
      }
    } catch (syncError) {
      console.error(
          "[OrganizationContext2] Error syncing subscription details " +
          "during organization creation:",
          syncError.message,
      );
    }

    // Step 5: Update State
    /* ------------------------------------------------------------
     * IMPORTANT
     * - OrganizationContext2 is NOT the runtime source of truth for
     *   subscription-management UI state.
     * - BillingContext owns the live billing surface and should be the
     *   primary reader for:
     *     • settled subscription state
     *     • pendingStripeChange
     *     • invoice reconciliation
     *
     * This local object is still useful during org bootstrap so the app
     * has a coherent initial org shape before BillingContext finishes
     * hydrating from Firestore.
     * ---------------------------------------------------------- */
    const newOrganization = {
      id: orgRef.id,
      name: orgName,
      stripeCustomerId,
      subscriptionId:
        syncedBillingData?.subscriptionId || subscriptionId || null,
      stripeSubscriptionItemId:
        syncedBillingData?.stripeSubscriptionItemId ||
        stripeSubscriptionItemId ||
        null,
      subscriptionStatus:
        syncedBillingData?.subscriptionStatus ||
        subscriptionStatus ||
        "inactive",
      trialStartsAt:
        syncedBillingData?.trialStartsAt ||
        subscriptionTrialStartsAt ||
        null,
      trialEndsAt:
        syncedBillingData?.trialEndsAt ||
        subscriptionTrialEndsAt ||
        (
          subscriptionStatus === "trialing" ?
            buildTrialEndsAtFallback() :
            null
        ),
      ownerId: userId,
      ownerEmail: email,
      seatCount:
        Number.isFinite(Number(syncedBillingData?.seatCount)) ?
          Number(syncedBillingData.seatCount) :
          subscriptionSeatCount,
      lastBilledAmount: 0,
      hasValidPaymentMethod:
        syncedBillingData?.hasValidPaymentMethod === true,
      planKey:
        syncedBillingData?.planKey || subscriptionPlanKey || null,
      planName:
        syncedBillingData?.planName || subscriptionPlanKey || null,
      price:
        syncedBillingData?.price != null ?
          syncedBillingData.price :
          null,
      priceId:
        syncedBillingData?.priceId || subscriptionPriceId || null,
      interval:
        syncedBillingData?.interval || defaultInterval,
      renewalDate:
        syncedBillingData?.renewalDate || null,
      pendingStripeChange: null,
      pendingStripeSchedule: null,
      suspendedStripeSchedule: null,
    };

    setOrganization(newOrganization);
    prevOrganizationId.current = orgRef.id;

    // IMPORTANT:
    // Do NOT call setAuthState() here.
    // AuthContext should hydrate from Firebase auth state + Firestore profile.
    // Calling setAuthState with a non-Firebase user shape can cause resets.

    return orgRef.id;

  } catch (error) {
    console.error("[OrganizationContext2] Error creating organization:", error.message);
    throw error;
  } finally {
    setLoading(false, "OrganizationContext2");
  }
}, [setLoading, currentUser, userPermissions, userRole, userProfile, setAuthState]);




const updateOrganization = useCallback(async (updatedOrganization) => {
//  console.log('[OrganizationContext2] updateOrganization triggered for:', updatedOrganization);

  // Validate the organization ID and check for meaningful changes
  if (!updatedOrganization?.id || updatedOrganization.id !== organization?.id) {
    console.warn('[OrganizationContext2] Skipped update - Invalid ID or mismatched organization ID.');
    return;
  }

  const keysToCompare = [
    "name",
    "subscriptionStatus",
    "trialEndsAt",
    "stripeCustomerId",
    "hasValidPaymentMethod",
    "lastBilledAmount",
  ];

  // Check for differences in key fields using shallow comparison
  const hasChanges = keysToCompare.some(
    (key) => updatedOrganization[key] !== organization[key]
  );

  if (!hasChanges) {
//    console.log('[OrganizationContext2] Skipped update - No meaningful changes detected.');
    return;
  }

  // Validate ownership before proceeding with updates
  const isOwner = await isCurrentUserOwner();
  if (!isOwner) {
    console.error('[OrganizationContext2] User is not the owner. Update rejected.');
    throw new Error('You do not have permission to update this organization.');
  }

  // Prevent updates to ownership fields
  if (
    (updatedOrganization.ownerId && updatedOrganization.ownerId !== organization?.ownerId) ||
    (updatedOrganization.ownerEmail && updatedOrganization.ownerEmail !== organization?.ownerEmail)
  ) {
    console.error('[OrganizationContext2] Attempt to update ownership fields detected. Update rejected.');
    throw new Error('Ownership fields (ownerId, ownerEmail) cannot be modified.');
  }

  setLoading(true, 'OrganizationContext2');
  try {
//    console.log('[OrganizationContext2] Updating organization data in Firestore...');

    const orgRef = doc(db, 'organizations', updatedOrganization.id);

    // Perform the update in Firestore
    await updateDoc(orgRef, updatedOrganization);

//    console.log('[OrganizationContext2] Organization data updated in Firestore:', updatedOrganization);

    // Sync subscription details with Stripe after updating the organization
    if (updatedOrganization.stripeCustomerId) {
      const syncCallable = httpsCallable(functions, "syncStripeSubscriptionDetails");
      try {
        const syncResponse = await syncCallable({
          stripeCustomerId: updatedOrganization.stripeCustomerId,
        });
        if (syncResponse.data.success) {
//          console.log(
 //           '[OrganizationContext2] Subscription details successfully synced after organization update:',
 //           syncResponse.data.message
 //         );
        } else {
          console.warn(
            '[OrganizationContext2] Syncing subscription details failed:',
            syncResponse.data.message
          );
        }
      } catch (syncError) {
        console.error(
          '[OrganizationContext2] Error syncing subscription details after organization update:',
          syncError.message
        );
      }
    }

    // Update local state only if meaningful changes occurred
    setOrganization((prevOrganization) => {
      const updatedState = { ...prevOrganization, ...updatedOrganization };

      // Synchronize with `organizationRef` to avoid unnecessary re-fetches
      organizationRef.current = updatedState;

//      console.log('[OrganizationContext2] Updated organization state:', updatedState);
      return updatedState;
    });

    // Update the reference for the previously fetched organization ID
    prevOrganizationId.current = updatedOrganization.id;
  } catch (error) {
    console.error('[OrganizationContext2] Failed to update organization data:', error);
    throw error;
  } finally {
    setLoading(false, 'OrganizationContext2');
//    console.log('[OrganizationContext2] updateOrganization completed');
  }
}, [organization, setLoading, isCurrentUserOwner]);


  const contextValue = useMemo(() => ({
    organization,
    createOrganization,
    updateOrganization,
    setOrganization,
    isCurrentUserOwner,
    clearOrganizationCache,
}), [organization, createOrganization, updateOrganization, setOrganization, isCurrentUserOwner]);

  return (
    <OrganizationContext2.Provider value={contextValue}>
      {children}
    </OrganizationContext2.Provider>
  );
};

export default OrganizationProvider2;
